import { Box, Divider, Flex, VStack } from "@chakra-ui/react";
import { dayjs } from "@equidefi/i18n";
import type React from "react";
import { useMemo } from "react";
import { NumericFormat } from "react-number-format";
import { useParams } from "react-router";

import { VAULT_ROUTES } from "@/constants/routes";
import { useInvestment } from "@/hooks/useInvestments";
import { offeringHidesSecurityLabel } from "@equidefi/shared";
import { EXEMPTION_TYPE_DISPLAY, type StockType, StockTypeMap } from "@equidefi/shared/constants/offerings";
import { Heading, Text } from "@equidefi/ui";

import { VaultContainer } from "../../components";
import { InvestmentTitleCard, VaultPageHeader } from "../components";

interface DetailRowProps {
  label: string;
  value: React.ReactNode;
}

/**
 * DetailRow - Two-column label/value display
 *
 * Used for investment and offering details in a consistent format.
 * Label on left, value right-aligned.
 */
const DetailRow: React.FC<DetailRowProps> = ({ label, value }) => {
  return (
    <Flex justify="space-between" align="center" py={2}>
      <Text fontSize="md" m={0}>
        {label}
      </Text>
      <Text fontSize="md" fontWeight="normal" textAlign="right" m={0}>
        {value}
      </Text>
    </Flex>
  );
};

/**
 * InvestmentDetailsPage - Detailed view of investment and offering information
 *
 * Displays two cards:
 * 1. Investment Details - Amount invested, shares, share price, dates
 * 2. Offering Details - Total amount, shares, offering dates, issuer info
 *
 * Date format: M/DD/YYYY
 * Currency format: $X,XXX with proper thousand separators
 */
const InvestmentDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const investmentId = id ?? "";
  const { data: investment, isFetchedAfterMount } = useInvestment(investmentId, {
    refetchOnMount: true,
    enabled: Boolean(id),
  });

  const offering = investment?.offering;
  const issuer = investment?.issuer;

  const offeringLabel = useMemo(() => {
    const exemption = EXEMPTION_TYPE_DISPLAY[offering?.exemption_type ?? ""] ?? offering?.exemption_type;
    const stockType = offering?.stock_type
      ? ` · ${StockTypeMap[offering.stock_type as StockType] ?? offering.stock_type}`
      : "";
    return `${exemption}${stockType}`;
  }, [offering?.exemption_type, offering?.stock_type]);

  if (!isFetchedAfterMount || !investment) {
    return null;
  }

  const formatCurrency = (value: number | string | undefined | null) => {
    if (!value) return "$0";
    return <NumericFormat thousandSeparator displayType="text" fixedDecimalScale={false} prefix="$" value={value} />;
  };

  const formatNumber = (value: number | string | undefined | null) => {
    if (!value) return "0";
    return <NumericFormat thousandSeparator displayType="text" value={value} />;
  };

  const formatDate = (date: string | undefined | null) => {
    if (!date) return "-";
    return dayjs(date).format("M/DD/YYYY");
  };

  // Determine completion date based on investment status
  const completionDate = investment.status === "CLOSED" ? investment.modify_date : null;
  const hideSecurityLabel = offeringHidesSecurityLabel(offering);

  return (
    <VaultContainer pb="10" maxW={{ base: "560px", lg: "1040px" }}>
      <VaultPageHeader
        crumbs={[
          { label: "Portfolio", to: "/vault/dashboard" },
          { label: issuer?.name ?? "Issuer", to: VAULT_ROUTES.offering(offering?.id ?? "") },
          { label: offering?.name ?? "Offering", to: VAULT_ROUTES.offering(offering?.id ?? "") },
          { label: "Investment", to: VAULT_ROUTES.investmentDetails(investment.id) },
          { label: "Details" },
        ]}
        backTo={VAULT_ROUTES.investmentDetails(investment.id)}
        backLabel="Back To Investment"
      />

      <VStack spacing={4} align="stretch">
        <InvestmentTitleCard investment={investment} issuer={issuer} offering={offering} />

        {/* Investment Details Card */}
        <Box bg="white" borderRadius="md" boxShadow="md" p={6}>
          <Heading fontSize="2xl" fontWeight="bold" mb={4}>
            Investment Details
          </Heading>

          <DetailRow label="Amount Invested" value={formatCurrency(investment.amount)} />
          {!hideSecurityLabel && (
            <>
              <DetailRow label={offering?.security_type_plural} value={formatNumber(investment.quantity)} />
              <DetailRow label={`${offering?.security_type_label} Price`} value={formatCurrency(investment.price)} />
            </>
          )}

          <Divider my={3} />

          <DetailRow label="Date Started" value={formatDate(investment.create_date)} />
          {completionDate && <DetailRow label="Date Completed" value={formatDate(completionDate)} />}
        </Box>

        {/* Offering Details Card */}
        <Box bg="white" borderRadius="md" boxShadow="md" p={6}>
          <Heading fontSize="2xl" fontWeight="bold" mb={4}>
            Offering Details
          </Heading>

          <Heading fontSize="lg" fontWeight="bold" mb={1}>
            {issuer?.name}
          </Heading>

          {offering?.name && (
            <Text fontSize="lg" mb={1}>
              {offering.name}
            </Text>
          )}
          <Text mb={4} color="gray.700">
            {offeringLabel}
          </Text>

          <Divider my={3} />

          <DetailRow label="Total Amount" value={formatCurrency(offering?.total_amount)} />
          {!hideSecurityLabel && (
            <DetailRow label={`Total ${offering?.security_type_plural}`} value={formatNumber(offering?.total_units)} />
          )}

          <Divider my={3} />

          <DetailRow label="Offering Start Date" value={formatDate(offering?.launch_date)} />
          <DetailRow label="Offering End Date" value={formatDate(offering?.termination_date)} />
        </Box>
      </VStack>
    </VaultContainer>
  );
};

export default InvestmentDetailsPage;
